<template>
	<div class="Page-section" :class="classes">
		<div class="Page-section-inner" :class="innerClasses">
			<!-- @slot Default slot for any content -->
			<slot />
		</div>
	</div>
</template>

<script>
export default {
	name: 'CloPageSection',
	data() {
		return {};
	},
	props: {
		/**
		 * Adds border to bottom of section
		 */
		border: {
			type: Boolean,
			default: false,
		},
		/**
		 * Controls space inside the section: loose or flush
		 */
		padding: {
			type: String,
			default: null,
		},
		/**
		 * Controls max width of content inside section, uses Lichen breakpoint sizes: md, lg, xl, xxl
		 */
		width: {
			type: String,
			default: null,
		},
		/**
		 * Background color of the section
		 */
		color: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes: function () {
			let classes = [];
			// border
			if (this.border) {
				classes.push('Page-section--border');
			}
			// color
			if (this.color) {
				classes.push('Page-section--' + this.color);
			}
			// padding
			if (this.padding) {
				classes.push('Page-section--' + this.padding);
			}
			return classes.join(' ');
		},
		innerClasses: function () {
			let classes = [];
			// width
			if (this.width) {
				classes.push('Page-section-inner--' + this.width);
			}
			return classes.join(' ');
		},
	},
};
</script>

<style lang="scss">
/* ----------------
/* Page-section: full-width row with optional background color variants
---------------- */
.Page-section {
	padding: $space-md;
	@include breakpoint(large) {
		padding: $space-md $space-lg $space-md $space-lg;
	}

	/* Loose variant adds additional vertical padding at xxlarge */
	&--loose {
		@include breakpoint(xxlarge) {
			padding: $space-lg;
		}
	}

	/* Flush variant removes default squished padding */
	&--flush {
		padding: 0;
	}

	/* Footer variant pushes footer to bottom of screen. Requires Page--flex parent */
	&--footer {
		margin-top: auto;
	}

	/* Page-section background color variants */
	&--neutralLight {
		background-color: $color-neutral-0;
	}
	&--neutralDark {
		background-color: $color-neutral-5;
	}
	&--primaryLight {
		background-color: $color-primary-0;
	}
	&--primaryDark {
		background-color: $color-primary-5;
	}
	&--secondaryLight {
		background-color: $color-secondary-0;
	}
	&--secondaryDark {
		background-color: $color-secondary-5;
	}
	&--white {
		background-color: $color-white;
	}
	&--black {
		background-color: $color-black;
	}

	/* Border variant adds a subtle border on the bottom for visual separation */
	&--border {
		border-bottom-width: 1px;
		border-bottom-style: solid;
		border-bottom-color: $color-neutral-1; // default

		/* Different color treatments depending on section background color */
		&.Page-section--neutralLight {
			border-bottom-color: $color-white;
		}
		&.Page-section--neutralDark {
			border-bottom-color: mix($color-neutral-5, $color-white, 85%);
		}
		&.Page-section--primaryLight {
			border-bottom-color: $color-white;
		}
		&.Page-section--primaryDark {
			border-bottom-color: mix($color-primary-5, $color-white, 85%);
		}
		&.Page-section--secondaryLight {
			border-bottom-color: $color-white;
		}
		&.Page-section--secondaryDark {
			border-bottom-color: mix($color-secondary-5, $color-white, 85%);
		}
		&.Page-section--white {
			border-bottom-color: $color-neutral-0;
		}
		&.Page-section--black {
			border-bottom-color: $color-neutral-5;
		}
	}
}

/* ----------------
/* Page-section-inner: sets the max-width and padding for your main column of content
---------------- */
.Page-section-inner {
	margin: 0 auto;

	/* Page-section-inner variants for max-width */
	&--md {
		max-width: 768px; /* 768px */
	}
	&--lg {
		max-width: 1024px; /* 1024px */
	}
	&--xl {
		max-width: 1200px; /* 1200px */
	}
	&--xxl {
		max-width: 1440px; /* 1440px */
	}
}
</style>
